// Analytics events for ACCESS Registration (webreg).
// Hello Heart events taxonomy (RND-37449 / analytics-instrumentation.md):
//   - Event names: snake_case, sent verbatim to Mixpanel (e.g. view_medicare_coverage).
//     The readable "View Medicare Coverage" display name is configured in Mixpanel
//     Lexicon, not here. Enforced by events.lock.test.ts.
//   - Screen views: view_ + feature/flow. User actions: verb first. Server-side
//     outcomes on the user's behalf: passive (…_completed_successfully).
//   - Property keys: snake_case (e.g. source, gating_answer, webreg_user_id)
// Mixpanel stays curated (screens + intentful CTAs/outcomes) — not a tap firehose.

export const CHECK_ELIGIBILITY = 'check_webreg_eligibility';
// start_webreg always represents the first step of the web registration flow. Funnels start
// from it, so do not send it from any other point in the flow.
export const START_WEBREG = 'start_webreg';
export const END_WEBREG = 'end_webreg';
export const WEBREG_COMPLETED = 'webreg_completed_successfully';
export const SELECT_STEP_BACK = 'select_webreg_step_back';
export const GO_TO_HOMEPAGE = 'go_to_homepage';
export const SELECT_START_OVER = 'select_webreg_start_over';
export const SELECT_LANGUAGE = 'select_webreg_language';

export const OPEN_HELP_MENU = 'open_webreg_help_menu';
export const CLOSE_HELP_MENU = 'close_webreg_help_menu';
export const ASK_HELP = 'ask_help';

// ============ SCREEN VIEWS ============
export const VIEW_WELCOME = 'view_welcome';
export const VIEW_DETAILS_FORM = 'view_webreg_details_form';
export const VIEW_CONDITION_SCREENING = 'view_webreg_condition_screening';
export const VIEW_PROGRAMS = 'view_webreg_programs';
export const VIEW_TERMS = 'view_webreg_terms';
export const VIEW_SWITCH_PROVIDER = 'view_webreg_switch_provider';
export const VIEW_LAB_RESULTS = 'view_webreg_lab_results';
export const VIEW_LABS_OUTREACH = 'view_webreg_labs_outreach';
export const VIEW_LABS_PENDING = 'view_webreg_labs_pending';
export const VIEW_SHIPPING_DETAILS = 'view_webreg_shipping_details';
export const VIEW_DOWNLOAD_APP = 'view_webreg_download_app';
export const VIEW_ERROR = 'view_webreg_error';
export const VIEW_CONFIRM_DETAILS = 'view_webreg_confirm_details';
export const VIEW_API_ERROR = 'view_webreg_api_error';
export const CLOSE_API_ERROR = 'close_webreg_api_error';
export const VIEW_CRASH = 'view_webreg_crash';
export const SELECT_CRASH_CONTINUE = 'select_webreg_crash_continue';

export const SUBMIT_DETAILS_FORM = 'submit_webreg_details_form';
export const SELECT_DETAIL_FIELD = '_select_webreg_detail_field';
export const COMPLETE_DETAIL_FIELD = '_complete_webreg_detail_field';
export const SCROLL_DETAILS_FORM = '_scroll_webreg_details_form';
export const OPEN_MBI_HELP = 'open_webreg_mbi_help';
export const CLOSE_MBI_HELP = 'close_webreg_mbi_help';
export const SELECT_LEGAL_DOCUMENT = 'select_webreg_legal_document';

export const SUBMIT_CONDITION_SCREENING = 'submit_condition_screening';

export const SUBMIT_SWITCH_PROVIDER = 'submit_webreg_switch_provider';
export const PROVIDER_SWITCH_PROCESSED = 'webreg_provider_switch_processed';

export const SUBMIT_LAB_RESULTS = 'submit_webreg_lab_results';

// ============ SHIPPING ============
export const SELECT_SHIPPING_FIELD = '_select_webreg_shipping_field';
export const COMPLETE_SHIPPING_FIELD = '_complete_webreg_shipping_field';
export const SELECT_ADDRESS_SUGGESTION = '_select_webreg_address_suggestion';
export const SCROLL_SHIPPING_DETAILS = '_scroll_webreg_shipping_details';
export const SUBMIT_SHIPPING_DETAILS = 'submit_webreg_shipping_details';

export const SELECT_DOWNLOAD_APP = 'select_download_app';

export const SELECT_ERROR_RETRY = 'select_webreg_error_retry';
export const SELECT_REENTER_DETAILS = 'select_webreg_reenter_details';

// PHI NOTE: raw PHQ-9/GAD-7 scores and the Q9 response are intentionally NOT
// emitted as event properties. Only booleans + non-PHI resource ids are sent.
// Do not emit a crisis_gate_shown event — it is a near-direct PHI proxy for Q9.

// ============ ERRORS ============
export const API_CALL_FAILED = 'webreg_api_call_failed';
export const SESSION_STORAGE_FAILED = 'webreg_session_storage_failed';

export const API_ERROR_REASONS = {
  PROMO_CAP_REACHED: 'promo_cap_reached',
  PROMO_FAILED: 'promo_failed',
  SIGNUP_FAILED: 'signup_failed',
  SHIPPING_FAILED: 'shipping_failed',
} as const;

export type ApiErrorReason = (typeof API_ERROR_REASONS)[keyof typeof API_ERROR_REASONS];

export const API_SERVICES = {
  CMS_ELIGIBILITY: 'cms_eligibility',
  CMS_ELIGIBILITY_POLL: 'cms_eligibility_poll',
  CMS_ALIGNMENT: 'cms_alignment',
  CMS_ALIGNMENT_POLL: 'cms_alignment_poll',
  PROMO: 'promo',
  SIGNUP: 'signup',
  CLAIM: 'claim',
  CONSENTS: 'consents',
  REGISTRATION_FAILURE: 'registration_failure',
  SHIPPING: 'shipping',
  RECAPTCHA: 'recaptcha',
} as const;

export type ApiService = (typeof API_SERVICES)[keyof typeof API_SERVICES];

export const EVENT_SOURCES = {
  TOP_BAR: 'top_bar',
  SCREEN: 'screen',
  HELP_MENU: 'help_menu',
} as const;

export type EventSource = (typeof EVENT_SOURCES)[keyof typeof EVENT_SOURCES];

export const ENTRY_SOURCES = {
  INVITE: 'invite',
  DIRECT: 'direct',
} as const;

export type EntrySource = (typeof ENTRY_SOURCES)[keyof typeof ENTRY_SOURCES];

export const NOT_ELIGIBLE_REASONS = {
  NOT_MEDICARE: 'not_medicare',
  EXCLUDED_SERVICES: 'excluded_services',
  CLINICAL_EXCLUSION: 'clinical_exclusion',
  UNKNOWN_RESULT: 'unknown_result',
  MISSING_RESULT: 'missing_result',
  NO_HIGH_BLOOD_PRESSURE: 'no_high_blood_pressure',
} as const;

export type NotEligibleReason = (typeof NOT_ELIGIBLE_REASONS)[keyof typeof NOT_ELIGIBLE_REASONS];

export const ALL_EVENTS = {
  CHECK_ELIGIBILITY,
  START_WEBREG,
  END_WEBREG,
  WEBREG_COMPLETED,
  SELECT_STEP_BACK,
  GO_TO_HOMEPAGE,
  SELECT_START_OVER,
  SELECT_LANGUAGE,
  OPEN_HELP_MENU,
  CLOSE_HELP_MENU,
  ASK_HELP,
  VIEW_WELCOME,
  VIEW_DETAILS_FORM,
  VIEW_CONDITION_SCREENING,
  VIEW_PROGRAMS,
  VIEW_TERMS,
  VIEW_SWITCH_PROVIDER,
  VIEW_LAB_RESULTS,
  VIEW_LABS_OUTREACH,
  VIEW_LABS_PENDING,
  VIEW_SHIPPING_DETAILS,
  VIEW_DOWNLOAD_APP,
  VIEW_ERROR,
  VIEW_CONFIRM_DETAILS,
  VIEW_API_ERROR,
  CLOSE_API_ERROR,
  VIEW_CRASH,
  SELECT_CRASH_CONTINUE,
  SUBMIT_DETAILS_FORM,
  SELECT_DETAIL_FIELD,
  COMPLETE_DETAIL_FIELD,
  SCROLL_DETAILS_FORM,
  OPEN_MBI_HELP,
  CLOSE_MBI_HELP,
  SELECT_LEGAL_DOCUMENT,
  SUBMIT_CONDITION_SCREENING,
  SUBMIT_SWITCH_PROVIDER,
  PROVIDER_SWITCH_PROCESSED,
  SUBMIT_LAB_RESULTS,
  SELECT_SHIPPING_FIELD,
  COMPLETE_SHIPPING_FIELD,
  SELECT_ADDRESS_SUGGESTION,
  SCROLL_SHIPPING_DETAILS,
  SUBMIT_SHIPPING_DETAILS,
  SELECT_DOWNLOAD_APP,
  SELECT_ERROR_RETRY,
  SELECT_REENTER_DETAILS,
  API_CALL_FAILED,
  SESSION_STORAGE_FAILED,
} as const;
